// Derivadas da irrigação — puras, sem banco, compartilhadas entre client e servidor.
//
// mm e L/planta NUNCA são armazenados (spec §2.1): existe UMA definição de cada, aqui.
// Duas implementações (uma na tela, outra no boletim) é como dois números do mesmo dia
// passam a discordar — e ninguém descobre qual está certo.

import { CULTURA_CITROS } from "./culturas.js";

/** Como a lâmina do setor é LIDA. Vem da CULTURA do setor, nunca de cadastro próprio. */
export type UnidadeLeitura = "mm" | "L_PLANTA";

/**
 * Banana → mm/dia · Citros → L/planta/dia.
 *
 * Em gotejo, mm sobre a área total não significa nada: o L1 com 1 mm são ~17 L/planta.
 * Cultura desconhecida (setor sem variedade, cultura custom de outra org) cai em mm —
 * é a leitura genérica, e a tela mostra a área que a produziu.
 */
export function unidadeDaCultura(slugCultura: string | null | undefined): UnidadeLeitura {
  return slugCultura === CULTURA_CITROS ? "L_PLANTA" : "mm";
}

export const ROTULO_UNIDADE: Record<UnidadeLeitura, string> = {
  mm: "mm",
  L_PLANTA: "L/planta",
};

/** Lâmina em milímetros: volume ÷ área ÷ 10. `null` quando não há área cadastrada. */
export function laminaMm(volumeM3: number, areaHa: number | null | undefined): number | null {
  if (!areaHa || areaHa <= 0) return null;
  return volumeM3 / areaHa / 10;
}

/** Litros por planta: volume × 1000 ÷ plantas. `null` quando não há contagem de plantas. */
export function litrosPorPlanta(volumeM3: number, plantas: number | null | undefined): number | null {
  if (!plantas || plantas <= 0) return null;
  return (volumeM3 * 1000) / plantas;
}

/** A leitura do setor na unidade da cultura dele — um só ponto de decisão. */
export function leituraDoSetor(
  volumeM3: number,
  unidade: UnidadeLeitura,
  setor: { areaHa: number | null; plantas: number | null },
): number | null {
  return unidade === "L_PLANTA"
    ? litrosPorPlanta(volumeM3, setor.plantas)
    : laminaMm(volumeM3, setor.areaHa);
}

/** Vazão média do próprio setor/estação: volume ÷ tempo. `null` se não regou. */
export function vazaoMediaM3h(volumeM3: number, runtimeS: number): number | null {
  if (runtimeS <= 0) return null;
  return volumeM3 / (runtimeS / 3600);
}

// ─── Limites das validações (§4) ─────────────────────────────────────────────
// Ficam aqui, e não só no validador, porque a ⚙ da tela explica "±30%" ao usuário —
// e o texto e a regra não podem divergir.

/** Vazão implícita fora desta fração da cadastrada marca o dia como `divergente`. */
export const TOLERANCIA_VAZAO = 0.30;
/** Fator de calibração fora desta faixa marca o dia como `divergente`. */
export const FATOR_MIN = 0.90;
export const FATOR_MAX = 1.10;

/**
 * Fora DESTA faixa o dia é `rejeitada`, não `divergente`.
 *
 * O fator multiplica o volume de todos os setores. Errar 14% é dado ruim — entra
 * marcado, e alguém decide o que fazer. Errar 100× (um número perdido numa linha de
 * rodapé que por acaso tenha a unidade) não é dado ruim, é leitura errada, e reescala a
 * lâmina do dia inteiro. Nenhuma fazenda tem hidrômetro medindo metade nem o dobro do
 * que as válvulas somam.
 */
export const FATOR_ABSURDO_MIN = 0.5;
export const FATOR_ABSURDO_MAX = 2.0;

/**
 * Abaixo DESTA fração do total do próprio relatório, o hidrômetro não mediu — está parado.
 *
 * Não é leitura errada: é o relatório dizendo, certo, que o medidor não contou. De 16/09 a
 * 04/10/2026 o controlador mandou "Actual Flow 0,0" (e, em alguns dias, 1,8 · 4,9 · 115,7)
 * em dias de 1.600 a 2.800 m³ pelas estações — de 0% a 6%. A recusa por "distância
 * implausível" descartava o dia inteiro, TEMPOS inclusive, e a ingestão parou por três
 * semanas sem nada quebrar no código.
 *
 * Sem medição o volume do dia é o de sempre (tempo × vazão cadastrada), só que sem a
 * calibração, e o dia entra `divergente`. Entre esta fração e `FATOR_ABSURDO_MIN` o medidor
 * está contando e discorda pela metade — esse continua sendo caso de recusa.
 */
export const FATOR_MEDIDOR_PARADO = 0.10;

// ─── Metas e aderência (ciclo 2) ─────────────────────────────────────────────
// Uma definição só, aqui: dashboard, KPI e (no ciclo 3) boletim têm que dar o MESMO
// número, senão vira discussão sobre qual tela está certa.

/** ±10% em torno da meta é "na meta" — a faixa premiada do futuro KPI. */
export const TOLERANCIA_META = 0.10;

/**
 * Desvio mínimo para um setor virar LINHA NO BOLETIM.
 *
 * ⚠️ Deliberadamente MAIOR que `TOLERANCIA_META`, e não é inconsistência: a faixa do KPI
 * é de PERÍODO, e um dia de rega isolado oscila muito mais que a média do mês. O boletim
 * herdou os ±10% por acidente e o resultado foi um bloco que listava 12 dos 13 setores
 * todo dia — o mesmo destino do "Estoque desatualizado em 33 produtos", que já saiu do
 * bloco do Manejo por virar ruído diário. Bloco que grita todo dia é bloco que ninguém lê
 * no dia em que ele tem razão.
 *
 * 30% é o mesmo limiar que a feature já usa como "isto está realmente fora" na validação
 * de vazão (`TOLERANCIA_VAZAO`) — um número só para a mesma ideia.
 */
export const DESVIO_BOLETIM = 0.30;

/**
 * Aderência = aplicado ÷ meta.
 *
 * `null` quando não há meta — NUNCA 0. Meta ausente não é meta zero: o sistema não
 * inventa alvo, e um setor sem meta cadastrada aparece com o valor cru.
 */
export function aderencia(aplicado: number | null, meta: number | null): number | null {
  if (aplicado === null || meta === null || !isFinite(aplicado) || !isFinite(meta) || meta <= 0) return null;
  return aplicado / meta;
}

/** Onde a aderência caiu. `null` sem meta. */
export type FaixaAderencia = "deficit" | "na_meta" | "excesso";

export function faixaDaAderencia(a: number | null): FaixaAderencia | null {
  if (a === null || !isFinite(a)) return null;
  if (a > 1 + TOLERANCIA_META) return "excesso";
  if (a < 1 - TOLERANCIA_META) return "deficit";
  return "na_meta";
}

/**
 * A escala divergente da feature — azul (água demais) ↔ laranja (seco), cinza na faixa.
 *
 * ⚠️ NÃO trocar por verde/vermelho: em protanopia esta paleta tem ΔE 24,7 e o semáforo
 * tem 4,1 — ilegível. A cor também nunca é canal único: todo gráfico traz o número.
 */
export const COR_DA_FAIXA: Record<FaixaAderencia, string> = {
  excesso: "#2a78d6",
  na_meta: "#9aa093",
  deficit: "#eb6834",
};

/** Meses em pt-BR, na ordem da grade de metas. */
export const MESES_ABREV = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];

/** Mês (1–12) de uma data civil YYYY-MM-DD — sem `new Date`, que muda com o fuso. */
export function mesDaData(dataIso: string): number {
  return Number(dataIso.slice(5, 7));
}

/** hh:mm a partir de segundos — o "Tempo" da tabela de Registros. */
export function formatarDuracao(segundos: number): string {
  const total = Math.max(0, Math.round(segundos));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  return `${h}:${String(m).padStart(2, "0")}`;
}

/** Número em pt-BR com N casas — sem `toLocaleString`, que varia por runtime. */
export function numeroBR(valor: number | null | undefined, casas = 1): string {
  if (valor === null || valor === undefined || !isFinite(valor)) return "—";
  const fixo = valor.toFixed(casas);
  const [inteiro, decimais] = fixo.split(".");
  const comMilhar = inteiro.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  return decimais ? `${comMilhar},${decimais}` : comMilhar;
}
